/**
 * /dev/spots — internal review-and-edit tool for the spots dataset.
 *
 * Four modes:
 *   1. Table view — every spot listed, searchable + filterable.
 *   2. Edit drawer — click a row to open a form with every editable field
 *      and an OpenStreetMap iframe showing the coords.
 *   3. Curation walkthrough — "Next un-curated" button steps through
 *      the spots whose `defaultWindRange` flag is still set so wind
 *      ranges can be filled in one at a time.
 *   4. Create new — "+ New spot" opens an empty drawer with an editable
 *      id, posts to POST /__dev/spots on save.
 *
 * Save is via the dev-only `PUT/POST /__dev/spots[/:id]` middleware in
 * vite.config.ts → writes back to src/spots.json on disk. Never linked
 * from the app; only accessible by typing /dev/spots in the URL bar
 * during local dev.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Map, Marker, NavigationControl } from "@vis.gl/react-maplibre";
import type { MapRef, MapLayerMouseEvent } from "@vis.gl/react-maplibre";
import {
  COMPASS_DIRS,
  LAN_VALUES,
  type CompassDir,
  type Lan,
  type Region,
  type Spot,
} from "../lib/types";
import { REGIONS } from "../lib/regions";
import { distanceKm } from "../lib/utils";
// CARTO Voyager — keyless basemap with roads + labels. Used here instead
// of the production MAP_STYLE because MapTiler enforces an origin/port
// allowlist (vite.config.ts: port 3000), and the dev/spots page might run
// on any port when several worktrees are active in parallel.
const DEV_MAP_STYLE = "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json";
import styles from "./DevSpotsPage.module.css";

type Filter = "all" | "needs-curation" | "curated";

export function DevSpotsPage() {
  const [spots, setSpots] = useState<Spot[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [region, setRegion] = useState<Region | "all">("all");
  const [filter, setFilter] = useState<Filter>("all");
  const [editId, setEditId] = useState<string | null>(null);
  // Draft for a brand-new spot. When set, the drawer renders in
  // create mode; setting it back to null cancels the creation.
  const [creating, setCreating] = useState<Spot | null>(null);

  // ── Load on mount ───────────────────────────────────────────────────────
  useEffect(() => {
    fetch("/__dev/spots")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: Spot[]) => setSpots(data))
      .catch((err) => setLoadError(String(err)));
  }, []);

  // ── Derived filtered list ───────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!spots) return [];
    const q = search.trim().toLowerCase();
    return spots.filter((s) => {
      if (region !== "all" && s.region !== region) return false;
      if (filter === "needs-curation" && !s.defaultWindRange) return false;
      if (filter === "curated" && s.defaultWindRange) return false;
      if (!q) return true;
      const blob = [
        s.name,
        s.area,
        s.waterBody ?? "",
        s.subregion ?? "",
        s.note ?? "",
        (s.tags ?? []).join(" "),
      ]
        .join(" ")
        .toLowerCase();
      return blob.includes(q);
    });
  }, [spots, search, region, filter]);

  const counts = useMemo(() => {
    if (!spots) return { total: 0, needsCuration: 0 };
    return {
      total: spots.length,
      needsCuration: spots.filter((s) => s.defaultWindRange).length,
    };
  }, [spots]);

  const editingSpot = editId && spots ? spots.find((s) => s.id === editId) : null;

  // ── Save handler — PUT to dev middleware then merge into local state ────
  async function saveSpot(updated: Spot): Promise<void> {
    const res = await fetch(`/__dev/spots/${encodeURIComponent(updated.id)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updated),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? `HTTP ${res.status}`);
    }
    setSpots((prev) =>
      prev ? prev.map((s) => (s.id === updated.id ? updated : s)) : prev,
    );
  }

  // ── Create handler — POST to dev middleware then append to state ────────
  async function createSpot(newSpot: Spot): Promise<void> {
    const res = await fetch(`/__dev/spots`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newSpot),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? `HTTP ${res.status}`);
    }
    setSpots((prev) => (prev ? [...prev, newSpot] : [newSpot]));
    setCreating(null);
    // Open the freshly-created spot in edit mode so further tweaks
    // round-trip through PUT instead of accidentally re-POSTing.
    setEditId(newSpot.id);
  }

  function startCreate() {
    // Sensible defaults — south Sweden, mid-range wind, flagged as
    // needs-curation so it shows up in the walkthrough until edited.
    setEditId(null);
    setCreating({
      id: "",
      name: "",
      region: "south-sweden",
      area: "",
      coords: { lat: 55.6, lon: 13.0 },
      ideal: { dirs: [], speed_min: 8, speed_max: 26 },
      secondary: { dirs: [], speed_min: 0, speed_max: 0 },
      priority: 2,
      defaultWindRange: true,
    });
  }

  // ── Delete handler — DELETE to dev middleware then drop from state ──────
  async function deleteSpot(id: string): Promise<void> {
    const res = await fetch(`/__dev/spots/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? `HTTP ${res.status}`);
    }
    setSpots((prev) => (prev ? prev.filter((s) => s.id !== id) : prev));
    setEditId(null);
  }

  // ── Curation walkthrough — open the next un-curated spot ────────────────
  function nextUncurated() {
    if (!spots) return;
    const startIdx = editId ? spots.findIndex((s) => s.id === editId) : -1;
    // Search from after the current spot, then wrap around.
    for (let i = 1; i <= spots.length; i++) {
      const s = spots[(startIdx + i) % spots.length];
      if (s?.defaultWindRange) {
        setEditId(s.id);
        return;
      }
    }
  }

  // ── CSV export — for ad-hoc bulk edits in a spreadsheet ─────────────────
  function downloadCsv() {
    if (!spots) return;
    const cols = [
      "id", "name", "region", "subregion", "area", "waterBody", "lat", "lon",
      "ideal_dirs", "ideal_min", "ideal_max",
      "secondary_dirs", "secondary_min", "secondary_max",
      "priority", "defaultWindRange",
      "note", "water", "access",
      "hazards", "facilities", "waterTypes", "bestMonths", "tags",
    ];
    const escape = (v: unknown): string => {
      if (v === undefined || v === null) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = spots.map((s) => [
      s.id, s.name, s.region, s.subregion ?? "", s.area, s.waterBody ?? "",
      s.coords.lat, s.coords.lon,
      s.ideal.dirs.join(" "), s.ideal.speed_min, s.ideal.speed_max,
      s.secondary.dirs.join(" "), s.secondary.speed_min, s.secondary.speed_max,
      s.priority, s.defaultWindRange ? "1" : "",
      s.note ?? "", s.water ?? "", s.access ?? "",
      (s.hazards ?? []).join("|"),
      (s.facilities ?? []).join("|"),
      (s.waterTypes ?? []).join("|"),
      (s.bestMonths ?? []).join("|"),
      (s.tags ?? []).join("|"),
    ]);
    const csv = [cols, ...rows].map((row) => row.map(escape).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `spots-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Render ──────────────────────────────────────────────────────────────
  if (loadError) {
    return (
      <div className={styles.layout}>
        <p className={styles.error}>Failed to load /__dev/spots: {loadError}</p>
        <p className={styles.hint}>
          This page only works under <code>npm run dev</code>. The dev
          plugin in vite.config.ts isn't registered for production builds.
        </p>
      </div>
    );
  }

  if (!spots) {
    return (
      <div className={styles.layout}>
        <p className={styles.hint}>Loading…</p>
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      <header className={styles.toolbar}>
        <h1 className={styles.title}>spots editor</h1>
        <span className={styles.counts}>
          {filtered.length} / {counts.total}{" "}
          {counts.needsCuration > 0 && (
            <span className={styles.warn}>· {counts.needsCuration} need curation</span>
          )}
        </span>
        <input
          className={styles.search}
          placeholder="Search name / area / subregion / note…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className={styles.select}
          value={region}
          onChange={(e) => setRegion(e.target.value as Region | "all")}
        >
          <option value="all">All regions</option>
          {REGIONS.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </select>
        <select
          className={styles.select}
          value={filter}
          onChange={(e) => setFilter(e.target.value as Filter)}
        >
          <option value="all">All</option>
          <option value="needs-curation">Needs curation</option>
          <option value="curated">Curated</option>
        </select>
        <button type="button" className={styles.btn} onClick={nextUncurated}>
          Curate next →
        </button>
        <button type="button" className={styles.btnPrimary} onClick={startCreate}>
          + New spot
        </button>
        <button type="button" className={styles.btn} onClick={downloadCsv}>
          Download CSV
        </button>
      </header>

      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.colName}>Name</th>
            <th>Region</th>
            <th>Subregion</th>
            <th>Area</th>
            <th>Ideal dirs</th>
            <th className={styles.colNumeric}>Speed</th>
            <th>Status</th>
            <th>Note</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((s) => (
            <tr
              key={s.id}
              className={[
                styles.row,
                s.defaultWindRange ? styles.rowNeedsCuration : "",
                editId === s.id ? styles.rowSelected : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => setEditId(s.id)}
            >
              <td className={styles.colName}>{s.name}</td>
              <td>{s.region}</td>
              <td>{s.subregion ?? "—"}</td>
              <td>{s.area}</td>
              <td className={styles.dirs}>{s.ideal.dirs.join(" ") || "—"}</td>
              <td className={styles.colNumeric}>
                {s.ideal.speed_min}-{s.ideal.speed_max}
              </td>
              <td>
                {s.defaultWindRange ? (
                  <span className={styles.warn}>default</span>
                ) : (
                  <span className={styles.ok}>curated</span>
                )}
              </td>
              <td className={styles.notePreview}>{s.note?.slice(0, 100) ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {editingSpot && (
        <EditDrawer
          mode="edit"
          spot={editingSpot}
          allSpots={spots}
          onClose={() => setEditId(null)}
          onSave={saveSpot}
          onDelete={deleteSpot}
        />
      )}
      {creating && (
        <EditDrawer
          mode="create"
          spot={creating}
          allSpots={spots}
          onClose={() => setCreating(null)}
          onSave={createSpot}
          onDelete={async () => { /* unreachable in create mode */ }}
        />
      )}
    </div>
  );
}

// ── Edit drawer ─────────────────────────────────────────────────────────────

interface EditDrawerProps {
  /** "edit" = PUT, prev/next + delete visible; "create" = POST, id editable. */
  mode: "edit" | "create";
  spot: Spot;
  allSpots: Spot[];
  onClose: () => void;
  onSave: (updated: Spot) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

// Strip diacritics and non-alphanumerics to produce a stable, lowercase
// snake_case id from a free-text name. Matches the style of existing
// ids in spots.json ("falsterbo_kanalen", "fagelmara", etc.).
function slugify(name: string): string {
  const fold = name
    .toLowerCase()
    .replace(/[åä]/g, "a")
    .replace(/ö/g, "o")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    // Strip combining diacritics left behind by NFD decomposition.
    .replace(/[̀-ͯ]/g, "");
  return fold.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

// Parse a "lat, lon" string. Accepts comma or whitespace separation,
// optional surrounding whitespace, and trailing/leading parens (so
// pasting "(59.31, 18.30)" still works). Returns null on any parse error
// or out-of-range value.
function parseLatLon(raw: string): { lat: number; lon: number } | null {
  const cleaned = raw.replace(/[()]/g, "").trim();
  const parts = cleaned.split(/[\s,]+/).filter(Boolean);
  if (parts.length !== 2) return null;
  const lat = Number(parts[0]);
  const lon = Number(parts[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  return { lat, lon };
}

function formatLatLon(lat: number, lon: number): string {
  return `${lat}, ${lon}`;
}

interface LocationGuess {
  lan: Lan | null;
  /** Nominatim's `county`/`state` value as returned, kept so we can show
   *  the user what we tried to match when mapping to Lan fails. */
  lanRaw: string | null;
  area: string | null;
}

// Map Nominatim's Swedish admin name (e.g. "Stockholms län", "Skåne län",
// "Västra Götalands län") to one of the Lan values used in the dataset.
// Strips the " län" suffix and an optional trailing genitive "s".
function nominatimToLan(raw: string | undefined | null): Lan | null {
  if (!raw) return null;
  const stripped = raw.replace(/\s+län\s*$/i, "").trim();
  const candidates = [stripped, stripped.replace(/s$/, "")];
  for (const c of candidates) {
    const hit = LAN_VALUES.find((l) => l.toLowerCase() === c.toLowerCase());
    if (hit) return hit;
  }
  return null;
}

// Reverse-geocode (lat, lon) to a Swedish län + a human area name. Uses
// Nominatim (OSM); free, no key required. The accept-language=sv
// parameter keeps results in Swedish so they match the dataset.
async function reverseGeocodeLocation(
  lat: number,
  lon: number,
): Promise<LocationGuess> {
  const url =
    `https://nominatim.openstreetmap.org/reverse?` +
    `lat=${lat}&lon=${lon}` +
    `&format=jsonv2&accept-language=sv&addressdetails=1&zoom=14`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`Nominatim HTTP ${res.status}`);
  const json = (await res.json()) as {
    address?: Record<string, string>;
    error?: string;
  };
  if (json.error) throw new Error(`Nominatim: ${json.error}`);
  const addr = json.address ?? {};
  // Sweden's län show up as `county` in Nominatim. `state` is a fallback
  // for places that get tagged at admin_level 4 directly.
  const lanRaw = addr.county ?? addr.state ?? null;
  // Areas come in many flavours depending on zoom. Pick the most specific
  // populated-place name first, then fall back through the hierarchy.
  // hamlet sits below municipality because Nominatim often returns very
  // local hamlet names (e.g. "Egelsvik") that aren't recognisable; the
  // kommun is a safer default when no town/village is available.
  const areaRaw =
    addr.town ??
    addr.village ??
    addr.city ??
    addr.suburb ??
    addr.municipality ??
    addr.hamlet ??
    null;
  // Strip the trailing " kommun" so "Södertälje kommun" → "Södertälje".
  const area = areaRaw ? areaRaw.replace(/\s+kommun\s*$/i, "") : null;
  return { lan: nominatimToLan(lanRaw), lanRaw, area };
}

interface WaterCandidate {
  name: string;
  /** Distance to spot in metres (rough — taken from the result's centre tag
   *  or the first way node; Overpass doesn't sort by distance for us). */
  distanceM: number;
  /** Raw `place=` or `water=` value, shown so the user can sanity-check
   *  what kind of feature OSM thinks this is (lake vs bay vs strait). */
  kind: string;
}

// Query Overpass for named water polygons and place=sea/bay/strait
// features within `radiusM` of (lat, lon). Returns deduped candidates
// sorted by approximate distance.
async function findWaterBodies(
  lat: number,
  lon: number,
  radiusM = 2500,
): Promise<WaterCandidate[]> {
  // `out center` gives us a lat/lon for ways/relations so we can compute
  // a rough distance. `tags` would skip the geometry entirely but then
  // we'd have no way to rank.
  // OSM tagging for water bodies isn't consistent:
  //   - natural=water       → lakes, large inland water polygons
  //   - natural=bay         → bays
  //   - natural=strait      → straits / fjordar (Himmerfjärden lives here)
  //   - place=sea|ocean     → named seas and oceans (Östersjön / Baltic)
  //   - place=bay|strait    → some less-mapped features
  // We union all of them. Without the `[name]` filter Overpass returns
  // every unnamed water polygon nearby, which is useless.
  const query = `
    [out:json][timeout:20];
    (
      way(around:${radiusM},${lat},${lon})[natural~"^(water|bay|strait)$"][name];
      relation(around:${radiusM},${lat},${lon})[natural~"^(water|bay|strait)$"][name];
      way(around:${radiusM},${lat},${lon})[place~"^(sea|bay|strait|ocean)$"][name];
      relation(around:${radiusM},${lat},${lon})[place~"^(sea|bay|strait|ocean)$"][name];
    );
    out center 40;
  `;
  // Overpass mirrors. The main endpoint often returns 504 / 429 under
  // load; falling through to mirrors keeps the dev tool usable.
  const endpoints = [
    "https://overpass-api.de/api/interpreter",
    "https://overpass.kumi.systems/api/interpreter",
    "https://overpass.private.coffee/api/interpreter",
  ];
  let lastErr: unknown = new Error("no overpass endpoints tried");
  let res: Response | null = null;
  for (const url of endpoints) {
    try {
      const r = await fetch(url, { method: "POST", body: query });
      // Retry on the gateway/timeout/rate-limit codes that mean
      // "this mirror is busy", but surface other failures immediately.
      if (r.status === 504 || r.status === 502 || r.status === 429) {
        lastErr = new Error(`Overpass ${new URL(url).host} HTTP ${r.status}`);
        continue;
      }
      if (!r.ok) throw new Error(`Overpass HTTP ${r.status}`);
      res = r;
      break;
    } catch (err) {
      lastErr = err;
    }
  }
  if (!res) throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
  const json = (await res.json()) as {
    elements: Array<{
      tags?: Record<string, string>;
      center?: { lat: number; lon: number };
      lat?: number;
      lon?: number;
    }>;
  };
  // Quick distance estimate in metres (shared haversine, km → m).
  const distance = (la: number, lo: number) =>
    distanceKm({ lat, lon }, { lat: la, lon: lo }) * 1000;
  const seen = new Set<string>();
  const out: WaterCandidate[] = [];
  for (const el of json.elements) {
    const name = el.tags?.name;
    if (!name || seen.has(name)) continue;
    seen.add(name);
    const ctr = el.center ?? { lat: el.lat ?? lat, lon: el.lon ?? lon };
    out.push({
      name,
      distanceM: distance(ctr.lat, ctr.lon),
      kind: el.tags?.water ?? el.tags?.place ?? el.tags?.natural ?? "water",
    });
  }
  return out.sort((a, b) => a.distanceM - b.distanceM);
}

function EditDrawer({ mode, spot, allSpots, onClose, onSave, onDelete }: EditDrawerProps) {
  const isCreate = mode === "create";
  // Local form state — we don't mutate the parent's spots until Save.
  const [draft, setDraft] = useState<Spot>(spot);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  // In create mode, the id auto-fills from the name slug until the user
  // edits the id field manually — at which point we stop overwriting it.
  const [idTouched, setIdTouched] = useState(false);
  // Coords as a free-form string so the user can paste "lat, lon" in one
  // go. Kept separate from draft.coords so partial/invalid typing
  // doesn't blow away the numeric coords mid-edit.
  const [coordsInput, setCoordsInput] = useState(() =>
    formatLatLon(spot.coords.lat, spot.coords.lon),
  );
  const [coordsError, setCoordsError] = useState<string | null>(null);
  // Water-body suggestions from Overpass, populated by "Find from coords".
  const [waterCandidates, setWaterCandidates] = useState<WaterCandidate[] | null>(null);
  const [waterLoading, setWaterLoading] = useState(false);
  const [waterError, setWaterError] = useState<string | null>(null);
  // Reverse-geocoded location guess (Nominatim).
  const [locLoading, setLocLoading] = useState(false);
  const [locStatus, setLocStatus] = useState<string | null>(null);
  const mapRef = useRef<MapRef | null>(null);

  // Reset draft when the drawer's spot changes (curation walkthrough).
  useEffect(() => {
    setDraft(spot);
    setCoordsInput(formatLatLon(spot.coords.lat, spot.coords.lon));
    setCoordsError(null);
    setSaveError(null);
    setWaterCandidates(null);
    setWaterError(null);
    setLocStatus(null);
    // Recentre the map on the new spot.
    mapRef.current?.flyTo({
      center: [spot.coords.lon, spot.coords.lat],
      zoom: 12,
      duration: 600,
    });
  }, [spot]);

  // Esc to close.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Track index for "next/prev" navigation through the filtered set.
  const idx = allSpots.findIndex((s) => s.id === spot.id);
  const prev = idx > 0 ? allSpots[idx - 1] : null;
  const next = idx >= 0 && idx < allSpots.length - 1 ? allSpots[idx + 1] : null;

  function update<K extends keyof Spot>(key: K, value: Spot[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }
  // Apply a new lat/lon to draft.coords and the coords input string in
  // one go. Used by marker drag, map click, and successful text parse.
  function applyCoords(lat: number, lon: number) {
    setDraft((d) => ({ ...d, coords: { ...d.coords, lat, lon } }));
    setCoordsInput(formatLatLon(lat, lon));
    setCoordsError(null);
  }
  function handleCoordsInputChange(value: string) {
    setCoordsInput(value);
    const parsed = parseLatLon(value);
    if (parsed) {
      setDraft((d) => ({ ...d, coords: { ...d.coords, lat: parsed.lat, lon: parsed.lon } }));
      setCoordsError(null);
    } else if (value.trim() === "") {
      setCoordsError("required");
    } else {
      setCoordsError("expected: lat, lon");
    }
  }
  function handleMapClick(e: MapLayerMouseEvent) {
    applyCoords(e.lngLat.lat, e.lngLat.lng);
  }
  async function handleAutoFillLocation() {
    setLocLoading(true);
    setLocStatus(null);
    try {
      const guess = await reverseGeocodeLocation(draft.coords.lat, draft.coords.lon);
      // Only overwrite fields we have something useful for; don't blank
      // out a curator's manual edits with a Nominatim miss. (Spot.subregion
      // uses exactOptionalPropertyTypes — assigning `undefined` is rejected,
      // so we build the patch conditionally.)
      setDraft((d) => {
        const patch: Partial<Spot> = {};
        if (guess.lan) patch.subregion = guess.lan;
        if (guess.area) patch.area = guess.area;
        return { ...d, ...patch };
      });
      const filled: string[] = [];
      if (guess.lan) filled.push(`län: ${guess.lan}`);
      else if (guess.lanRaw) filled.push(`(unmatched län "${guess.lanRaw}")`);
      if (guess.area) filled.push(`area: ${guess.area}`);
      setLocStatus(filled.length ? `filled ${filled.join(", ")}` : "no match found");
    } catch (err) {
      setLocStatus(String(err));
    } finally {
      setLocLoading(false);
    }
  }
  async function handleFindWaterBody() {
    setWaterLoading(true);
    setWaterError(null);
    setWaterCandidates(null);
    try {
      const results = await findWaterBodies(draft.coords.lat, draft.coords.lon);
      setWaterCandidates(results);
      if (results.length === 0) {
        setWaterError("No named water bodies within 2.5 km. Try moving the marker closer to water.");
      }
    } catch (err) {
      setWaterError(String(err));
    } finally {
      setWaterLoading(false);
    }
  }
  function updateIdeal<K extends keyof Spot["ideal"]>(key: K, value: Spot["ideal"][K]) {
    setDraft((d) => ({ ...d, ideal: { ...d.ideal, [key]: value } }));
  }
  function updateSecondary<K extends keyof Spot["secondary"]>(key: K, value: Spot["secondary"][K]) {
    setDraft((d) => ({ ...d, secondary: { ...d.secondary, [key]: value } }));
  }
  function toggleDir(scope: "ideal" | "secondary", dir: CompassDir) {
    setDraft((d) => {
      const range = d[scope];
      const dirs = range.dirs.includes(dir)
        ? range.dirs.filter((x) => x !== dir)
        : [...range.dirs, dir];
      return { ...d, [scope]: { ...range, dirs } };
    });
  }

  // In create mode the id must be present and unique; in edit mode the
  // primary button is always live (the id is immutable and unique by
  // construction).
  const idCollision =
    isCreate && draft.id !== "" && allSpots.some((s) => s.id === draft.id);
  const saveDisabled =
    isCreate && (draft.id === "" || draft.name === "" || idCollision);

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      // Curation status is now driven by the explicit checkbox below;
      // we don't auto-strip the flag here. The checkbox writes
      // defaultWindRange: true|undefined, and saving persists whatever
      // state the user set.
      const cleaned: Spot = { ...draft };
      if (!cleaned.defaultWindRange) {
        delete (cleaned as { defaultWindRange?: boolean }).defaultWindRange;
      }
      await onSave(cleaned);
      setDraft(cleaned);
    } catch (err) {
      setSaveError(String(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    const ok = window.confirm(
      `Delete "${spot.name}" (${spot.id})? This writes src/spots.json and can be reverted with git.`,
    );
    if (!ok) return;
    setDeleting(true);
    setSaveError(null);
    try {
      await onDelete(spot.id);
    } catch (err) {
      setSaveError(String(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <div className={styles.scrim} onClick={onClose} />
      <aside className={styles.drawer}>
        <header className={styles.drawerHeader}>
          <h2 className={styles.drawerTitle}>
            {isCreate ? (draft.name || "New spot") : spot.name}
          </h2>
          <span className={styles.drawerSubtitle}>
            {isCreate ? (draft.id || "(id auto-fills from name)") : spot.id}
          </span>
          <div className={styles.drawerActions}>
            {!isCreate && (
              <>
                <button
                  type="button"
                  className={styles.btn}
                  disabled={!prev}
                  onClick={() => prev && (window.location.hash = "")}
                >
                  ← prev (#{idx})
                </button>
                <button
                  type="button"
                  className={styles.btn}
                  disabled={!next}
                  onClick={() => next && (window.location.hash = "")}
                >
                  next (#{idx + 2}) →
                </button>
              </>
            )}
            <button type="button" className={styles.btn} onClick={onClose}>
              ✕ {isCreate ? "cancel" : "close"} (Esc)
            </button>
          </div>
        </header>

        <div className={styles.drawerBody}>
          {/* Map preview — interactive MapLibre with a draggable marker.
              Click anywhere on the map to reposition the marker; drag
              the marker for fine adjustments. Both flows update the
              "Coordinates" input below. */}
          <div className={styles.map}>
            <Map
              ref={mapRef}
              mapStyle={DEV_MAP_STYLE}
              initialViewState={{
                longitude: draft.coords.lon,
                latitude: draft.coords.lat,
                zoom: 12,
              }}
              minZoom={3}
              maxZoom={18}
              dragRotate={false}
              pitchWithRotate={false}
              touchPitch={false}
              style={{ width: "100%", height: "100%" }}
              onClick={handleMapClick}
            >
              <NavigationControl position="top-right" showCompass={false} />
              {/* Wind compass — non-interactive overlay centred on the
                  spot. Rendered before the draggable pin so the pin
                  paints on top. */}
              <Marker
                longitude={draft.coords.lon}
                latitude={draft.coords.lat}
                anchor="center"
              >
                <div className={styles.compassWrap}>
                  <WindCompass idealDirs={draft.ideal.dirs} />
                </div>
              </Marker>
              <Marker
                longitude={draft.coords.lon}
                latitude={draft.coords.lat}
                anchor="bottom"
                draggable
                onDragEnd={(e) => applyCoords(e.lngLat.lat, e.lngLat.lng)}
              >
                <span className={styles.mapMarker} aria-label="Spot location" />
              </Marker>
            </Map>
          </div>

          <Row>
            <Field label="Name">
              <input
                className={styles.input}
                value={draft.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setDraft((d) => ({
                    ...d,
                    name,
                    // Keep id in sync with name until the curator picks
                    // their own id — once they touch the id field, we
                    // stop overwriting their value.
                    ...(isCreate && !idTouched ? { id: slugify(name) } : {}),
                  }));
                }}
              />
            </Field>
            {isCreate && (
              <Field
                label={
                  draft.id && allSpots.some((s) => s.id === draft.id)
                    ? `ID — already exists`
                    : !draft.id
                      ? `ID — required`
                      : `ID (lowercase, no spaces)`
                }
              >
                <input
                  className={styles.input}
                  value={draft.id}
                  onChange={(e) => {
                    setIdTouched(true);
                    update("id", e.target.value);
                  }}
                  placeholder="auto from name"
                />
              </Field>
            )}
          </Row>

          <div className={styles.locationHeader}>
            <button
              type="button"
              className={styles.btn}
              onClick={handleAutoFillLocation}
              disabled={locLoading}
            >
              {locLoading ? "looking up…" : "Auto-fill län + area from coords"}
            </button>
            {locStatus && <span className={styles.locStatus}>{locStatus}</span>}
          </div>
          <Row>
            <Field label="Region">
              <select
                className={styles.input}
                value={draft.region}
                onChange={(e) => update("region", e.target.value as Region)}
              >
                {REGIONS.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Subregion (län)">
              <input
                className={styles.input}
                value={draft.subregion ?? ""}
                onChange={(e) =>
                  update("subregion", (e.target.value || undefined) as Spot["subregion"])
                }
                placeholder="leave empty for Öland"
              />
            </Field>
            <Field label="Area">
              <input
                className={styles.input}
                value={draft.area}
                onChange={(e) => update("area", e.target.value)}
              />
            </Field>
          </Row>

          <Field label="Body of water (optional)">
            <div className={styles.waterRow}>
              <input
                className={styles.input}
                value={draft.waterBody ?? ""}
                onChange={(e) =>
                  update("waterBody", (e.target.value || undefined) as Spot["waterBody"])
                }
                placeholder="e.g. Himmerfjärden, Mälaren, Östersjön"
              />
              <button
                type="button"
                className={styles.btn}
                onClick={handleFindWaterBody}
                disabled={waterLoading}
              >
                {waterLoading ? "searching…" : "Find from coords"}
              </button>
            </div>
            {waterError && <span className={styles.error}>{waterError}</span>}
            {waterCandidates && waterCandidates.length > 0 && (
              <div className={styles.waterCandidates}>
                {waterCandidates.map((c) => (
                  <button
                    type="button"
                    key={`${c.name}-${Math.round(c.distanceM)}`}
                    className={styles.waterCandidate}
                    onClick={() => {
                      update("waterBody", c.name);
                      setWaterCandidates(null);
                    }}
                  >
                    <span className={styles.waterCandidateName}>{c.name}</span>
                    <span className={styles.waterCandidateMeta}>
                      {c.kind} · {Math.round(c.distanceM)} m
                    </span>
                  </button>
                ))}
              </div>
            )}
          </Field>

          <Row>
            <Field label={coordsError ? `Coordinates — ${coordsError}` : "Coordinates (lat, lon)"}>
              <input
                className={styles.input}
                value={coordsInput}
                onChange={(e) => handleCoordsInputChange(e.target.value)}
                placeholder="59.3293, 18.0686"
              />
            </Field>
            <Field label="Priority">
              <input
                className={styles.input}
                type="number"
                min="1"
                max="3"
                value={draft.priority}
                onChange={(e) => update("priority", Number(e.target.value))}
              />
            </Field>
          </Row>

          {/* Curation status — explicit toggle so you can mark a spot
              as "still needs curation" after coming back to it later. */}
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={!!draft.defaultWindRange}
              onChange={(e) =>
                update("defaultWindRange", e.target.checked || undefined)
              }
            />
            <span>
              <strong>Needs curation</strong> — flags this spot in the table
              and "Curate next →" walkthrough. Uncheck once happy.
            </span>
          </label>

          {/* Ideal wind */}
          <fieldset className={styles.fieldset}>
            <legend>
              Ideal wind
              {draft.defaultWindRange && (
                <span className={styles.warn}> · marked needs-curation</span>
              )}
            </legend>
            <DirGrid
              selected={draft.ideal.dirs}
              onToggle={(d) => toggleDir("ideal", d)}
            />
            <Row>
              <Field label="Speed min (m/s)">
                <input
                  className={styles.input}
                  type="number"
                  min="0"
                  max="40"
                  value={draft.ideal.speed_min}
                  onChange={(e) => updateIdeal("speed_min", Number(e.target.value))}
                />
              </Field>
              <Field label="Speed max (m/s)">
                <input
                  className={styles.input}
                  type="number"
                  min="0"
                  max="40"
                  value={draft.ideal.speed_max}
                  onChange={(e) => updateIdeal("speed_max", Number(e.target.value))}
                />
              </Field>
            </Row>
          </fieldset>

          {/* Secondary wind */}
          <fieldset className={styles.fieldset}>
            <legend>Secondary wind (optional)</legend>
            <DirGrid
              selected={draft.secondary.dirs}
              onToggle={(d) => toggleDir("secondary", d)}
            />
            <Row>
              <Field label="Speed min (m/s)">
                <input
                  className={styles.input}
                  type="number"
                  min="0"
                  max="40"
                  value={draft.secondary.speed_min}
                  onChange={(e) =>
                    updateSecondary("speed_min", Number(e.target.value))
                  }
                />
              </Field>
              <Field label="Speed max (m/s)">
                <input
                  className={styles.input}
                  type="number"
                  min="0"
                  max="40"
                  value={draft.secondary.speed_max}
                  onChange={(e) =>
                    updateSecondary("speed_max", Number(e.target.value))
                  }
                />
              </Field>
            </Row>
          </fieldset>

          <Field label="Note">
            <textarea
              className={styles.textarea}
              rows={6}
              value={draft.note ?? ""}
              onChange={(e) => update("note", e.target.value)}
            />
          </Field>

          <Row>
            <Field label="Water">
              <textarea
                className={styles.textarea}
                rows={3}
                value={draft.water ?? ""}
                onChange={(e) => update("water", e.target.value)}
              />
            </Field>
            <Field label="Access">
              <textarea
                className={styles.textarea}
                rows={3}
                value={draft.access ?? ""}
                onChange={(e) => update("access", e.target.value)}
              />
            </Field>
          </Row>

          <Row>
            <Field label="Hazards (comma sep)">
              <input
                className={styles.input}
                value={(draft.hazards ?? []).join(", ")}
                onChange={(e) =>
                  update(
                    "hazards",
                    e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  )
                }
              />
            </Field>
            <Field label="Facilities (comma sep)">
              <input
                className={styles.input}
                value={(draft.facilities ?? []).join(", ")}
                onChange={(e) =>
                  update(
                    "facilities",
                    e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  )
                }
              />
            </Field>
          </Row>

          <Row>
            <Field label="Water types (comma sep)">
              <input
                className={styles.input}
                value={(draft.waterTypes ?? []).join(", ")}
                onChange={(e) =>
                  update(
                    "waterTypes",
                    e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  )
                }
              />
            </Field>
            <Field label="Best months (comma sep)">
              <input
                className={styles.input}
                value={(draft.bestMonths ?? []).join(", ")}
                onChange={(e) =>
                  update(
                    "bestMonths",
                    e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  )
                }
              />
            </Field>
          </Row>

          <Field label="Tags (Enter to add — free-form, searchable)">
            <TagsInput
              value={draft.tags ?? []}
              onChange={(next) => update("tags", next)}
              placeholder="e.g. Beginner friendly, then add another with Enter"
            />
          </Field>
        </div>

        <footer className={styles.drawerFooter}>
          {saveError && <span className={styles.error}>{saveError}</span>}
          {!isCreate && (
            <button
              type="button"
              className={styles.btnDanger}
              onClick={handleDelete}
              disabled={deleting || saving}
            >
              {deleting ? "deleting…" : "delete spot"}
            </button>
          )}
          <span className={styles.footerSpacer} />
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleSave}
            disabled={saving || deleting || saveDisabled}
          >
            {saving ? "saving…" : isCreate ? "create spot" : "save"}
          </button>
        </footer>
      </aside>
    </>
  );
}

// ── Small helpers ───────────────────────────────────────────────────────────

// Chip-style tags editor. Press Enter (or Tab) to commit the typed
// text as a new tag, click × on a chip to remove, Backspace on an
// empty input removes the last chip. Lets tag values contain commas,
// which is the whole point of having this instead of a plain comma-
// separated input.
function TagsInput({
  value,
  onChange,
  placeholder,
}: {
  value: readonly string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");
  function commit() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (value.includes(trimmed)) {
      setDraft("");
      return;
    }
    onChange([...value, trimmed]);
    setDraft("");
  }
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === "Tab") {
      if (draft.trim()) {
        e.preventDefault();
        commit();
      }
    } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
  }
  return (
    <div className={styles.tagsField}>
      {value.map((t) => (
        <span key={t} className={styles.tagChip}>
          <span>{t}</span>
          <button
            type="button"
            className={styles.tagChipRemove}
            aria-label={`Remove ${t}`}
            onClick={() => onChange(value.filter((x) => x !== t))}
          >
            ×
          </button>
        </span>
      ))}
      <input
        className={styles.tagInput}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}
        placeholder={value.length === 0 ? placeholder : ""}
      />
    </div>
  );
}

// ── Wind compass overlay ────────────────────────────────────────────────────
// Renders a 16-petal compass centred on the spot, with the selected
// directions filled. Mirrors the DirGrid below the map so curators see
// the arc grow as they toggle directions. Two layers: secondary (faint
// outline) underneath, ideal (solid fill) on top.

function dirsToArcs(
  dirs: readonly CompassDir[],
): Array<{ startDeg: number; endDeg: number }> {
  const idxs = dirs
    .map((d) => COMPASS_DIRS.indexOf(d))
    .filter((i): i is number => i >= 0)
    .sort((a, b) => a - b);
  const first = idxs[0];
  if (first === undefined) return [];
  if (idxs.length === 16) return [{ startDeg: 0, endDeg: 360 }];
  // Group consecutive indices into runs.
  const runs: number[][] = [];
  let current: number[] = [first];
  for (let i = 1; i < idxs.length; i++) {
    const here = idxs[i] as number;
    const prev = idxs[i - 1] as number;
    if (here === prev + 1) current.push(here);
    else {
      runs.push(current);
      current = [here];
    }
  }
  runs.push(current);
  // Stitch wrap-around (last run ends at 15, first starts at 0) into one.
  const firstRun = runs[0];
  const lastRun = runs[runs.length - 1];
  if (
    runs.length > 1 &&
    firstRun &&
    lastRun &&
    firstRun[0] === 0 &&
    lastRun[lastRun.length - 1] === 15
  ) {
    runs.pop();
    runs[0] = [...lastRun, ...firstRun];
  }
  return runs.map((run) => {
    const startIdx = run[0] as number;
    const endIdx = run[run.length - 1] as number;
    // Each compass cell spans 22.5° centred on its index. Pad ±11.25°
    // so adjacent selected cells merge into a single seamless arc.
    let startDeg = startIdx * 22.5 - 11.25;
    let endDeg = endIdx * 22.5 + 11.25;
    if (endDeg <= startDeg) endDeg += 360;
    return { startDeg, endDeg };
  });
}

function compassPoint(deg: number, r: number): { x: number; y: number } {
  // SVG y-axis is down, compass 0° is up (north). x = r·sin, y = −r·cos.
  const rad = (deg * Math.PI) / 180;
  return { x: r * Math.sin(rad), y: -r * Math.cos(rad) };
}

function arcPath(startDeg: number, endDeg: number, r: number): string {
  const sweep = endDeg - startDeg;
  if (sweep >= 360) {
    // Full circle — SVG can't draw 360° in a single elliptical arc, so
    // we compose it from two halves.
    return `M 0 -${r} A ${r} ${r} 0 1 1 0 ${r} A ${r} ${r} 0 1 1 0 -${r} Z`;
  }
  const p1 = compassPoint(startDeg, r);
  const p2 = compassPoint(endDeg, r);
  const largeArc = sweep > 180 ? 1 : 0;
  return `M 0 0 L ${p1.x} ${p1.y} A ${r} ${r} 0 ${largeArc} 1 ${p2.x} ${p2.y} Z`;
}

function WindCompass({ idealDirs }: { idealDirs: readonly CompassDir[] }) {
  const R = 70;
  const idealArcs = dirsToArcs(idealDirs);
  // Halo arcs — same arcs widened by one compass cell (22.5°) on each
  // end. Communicates the "blue / adjacent" tier on the map markers:
  // wind in this band is one cell out, within forecast tolerance.
  const haloArcs = idealArcs.map((a) => ({
    startDeg: a.startDeg - 22.5,
    endDeg: a.endDeg + 22.5,
  }));
  return (
    <svg
      className={styles.compass}
      viewBox={`-${R + 14} -${R + 14} ${(R + 14) * 2} ${(R + 14) * 2}`}
      width={(R + 14) * 2}
      height={(R + 14) * 2}
      aria-hidden
    >
      {/* Outer ring. */}
      <circle r={R} fill="rgba(0, 0, 0, 0.25)" stroke="white" strokeWidth={1.5} />
      {/* Teal halo — drawn first so the solid green ideal arc paints
          on top, leaving only the ±22.5° tolerance bands visible
          either side of each ideal arc. */}
      {haloArcs.map((a, i) => (
        <path
          key={`h-${i}`}
          d={arcPath(a.startDeg, a.endDeg, R)}
          fill="rgba(28, 209, 176, 0.55)"
        />
      ))}
      {/* Ideal arcs — solid green core. */}
      {idealArcs.map((a, i) => (
        <path
          key={`i-${i}`}
          d={arcPath(a.startDeg, a.endDeg, R)}
          fill="rgba(0, 220, 90, 0.85)"
          stroke="rgba(0, 255, 110, 1)"
          strokeWidth={1.5}
        />
      ))}
      {/* Cardinal labels. */}
      {(
        [
          { deg: 0, label: "N" },
          { deg: 90, label: "E" },
          { deg: 180, label: "S" },
          { deg: 270, label: "W" },
        ] as const
      ).map(({ deg, label }) => {
        const p = compassPoint(deg, R + 8);
        return (
          <text
            key={label}
            x={p.x}
            y={p.y}
            textAnchor="middle"
            dominantBaseline="central"
            fill="white"
            fontSize={11}
            fontWeight={700}
            fontFamily="ui-monospace, monospace"
          >
            {label}
          </text>
        );
      })}
      {/* Centre dot. */}
      <circle r={2.5} fill="white" />
    </svg>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className={styles.formRow}>{children}</div>;
}

function DirGrid({
  selected,
  onToggle,
}: {
  selected: readonly CompassDir[];
  onToggle: (d: CompassDir) => void;
}) {
  return (
    <div className={styles.dirGrid}>
      {COMPASS_DIRS.map((d) => (
        <button
          type="button"
          key={d}
          className={[
            styles.dirCell,
            selected.includes(d) ? styles.dirCellOn : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={() => onToggle(d)}
        >
          {d}
        </button>
      ))}
    </div>
  );
}
